// components/ui/FormFields.tsx
import React, { ReactNode } from "react";

const inputBase = "w-full px-3 py-2 rounded-xl border border-gray-200/70 bg-white/60 backdrop-blur-sm text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition-all placeholder-gray-400 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100";
const labelBase = "text-sm font-medium text-gray-700 mb-1 block";
const errorBase = "text-xs text-red-500 mt-1";
const helperBase = "text-xs text-gray-500 mt-1";

// ─── Input Component ─────────────────────────────────────────────────────────
export interface InputProps {
  label?: string;
  value: string | number;
  onChange: (v: string) => void;
  type?: "text" | "email" | "password" | "number" | "tel" | "url" | "search" | "date" | "time" | "datetime-local";
  name?: string;
  id?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  error?: string;
  helperText?: string;
  step?: string | number;
  min?: string | number;
  max?: string | number;
  pattern?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  maxLength?: number;
  minLength?: number;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  isLoading?: boolean;
  clearable?: boolean;
  onClear?: () => void;
}

export const Input: React.FC<InputProps> = ({
  label,
  value,
  onChange,
  type = "text",
  name,
  id,
  placeholder = "",
  required = false,
  disabled = false,
  readOnly = false,
  className = "",
  inputClassName = "",
  labelClassName = "",
  error,
  helperText,
  step,
  min,
  max,
  pattern,
  autoFocus = false,
  autoComplete,
  maxLength,
  minLength,
  onBlur,
  onFocus,
  onKeyPress,
  leftIcon,
  rightIcon,
  isLoading = false,
  clearable = false,
  onClear,
}) => (
  <div className={`space-y-1 ${className}`}>
    {label && (
      <label 
        htmlFor={id || name} 
        className={`${labelBase} ${labelClassName} ${required ? "after:content-['*'] after:ml-1 after:text-red-500" : ""}`}
      >
        {label}
      </label>
    )}
    <div className="relative">
      {leftIcon && (
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          {leftIcon}
        </div>
      )}
      <input
        id={id || name}
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled || isLoading}
        readOnly={readOnly}
        step={step}
        min={min}
        max={max}
        pattern={pattern}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        maxLength={maxLength}
        minLength={minLength}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyPress={onKeyPress}
        className={`
          ${inputBase} 
          ${leftIcon ? "pl-10" : ""} 
          ${rightIcon || clearable ? "pr-10" : ""}
          ${error ? "border-red-500 focus:ring-red-400" : ""}
          ${inputClassName}
        `}
      />
      {(rightIcon || (clearable && value && !disabled && !readOnly)) && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-500 border-t-transparent"></div>
          ) : clearable && value && !disabled && !readOnly ? (
            <button
              type="button"
              onClick={onClear}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          ) : (
            rightIcon
          )}
        </div>
      )}
    </div>
    {error && <p className={errorBase}>{error}</p>}
    {helperText && !error && <p className={helperBase}>{helperText}</p>}
  </div>
);

// ─── Select Component ────────────────────────────────────────────────────────
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: ReactNode;
  group?: string;
}

export interface SelectProps {
  label?: string;
  value: string | string[];
  onChange: (value: string) => void;
  options?: SelectOption[];
  children?: React.ReactNode;
  name?: string;
  id?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  error?: string;
  helperText?: string;
  multiple?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  onSearch?: (searchTerm: string) => void;
  loading?: boolean;
  noOptionsMessage?: string;
}

export const Select: React.FC<SelectProps> = ({
  label,
  value,
  onChange,
  options,
  children,
  name,
  id,
  placeholder = "Select an option",
  required = false,
  disabled = false,
  className = "",
  error,
  helperText,
  multiple = false,
  searchable = false,
  clearable = false,
  onSearch,
  loading = false,
  noOptionsMessage = "No options available",
}) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);
  const selectRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options?.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options?.find(opt => opt.value === value);

  return (
    <div className={`space-y-1 ${className}`} ref={selectRef}>
      {label && (
        <label htmlFor={id || name} className={`${labelBase} ${required ? "after:content-['*'] after:ml-1 after:text-red-500" : ""}`}>
          {label}
        </label>
      )}
      
      {searchable ? (
        // Searchable Select
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              onSearch?.(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            className={`${inputBase} ${error ? "border-red-500 focus:ring-red-400" : ""}`}
          />
          {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-500">Loading...</div>
              ) : filteredOptions?.length === 0 ? (
                <div className="p-4 text-center text-gray-500">{noOptionsMessage}</div>
              ) : (
                filteredOptions?.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setSearchTerm(opt.label);
                      setIsOpen(false);
                    }}
                    disabled={opt.disabled}
                    className={`
                      w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors
                      ${opt.disabled ? "opacity-50 cursor-not-allowed" : ""}
                      ${value === opt.value ? "bg-indigo-50 text-indigo-700" : ""}
                    `}
                  >
                    <div className="flex items-center gap-2">
                      {opt.icon}
                      {opt.label}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      ) : (
        // Standard Select
        <select
          id={id || name}
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          disabled={disabled}
          multiple={multiple}
          className={`
            ${inputBase}
            ${error ? "border-red-500 focus:ring-red-400" : ""}
            ${multiple ? "h-auto min-h-[100px]" : ""}
          `}
        >
          {placeholder && !multiple && (
            <option value="" disabled>{placeholder}</option>
          )}
          {options
            ? options.map((opt) => (
                <option 
                  key={opt.value} 
                  value={opt.value} 
                  disabled={opt.disabled}
                >
                  {opt.label}
                </option>
              ))
            : children}
        </select>
      )}
      
      {clearable && value && !multiple && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="text-xs text-indigo-600 hover:text-indigo-700"
        >
          Clear
        </button>
      )}
      
      {error && <p className={errorBase}>{error}</p>}
      {helperText && !error && <p className={helperBase}>{helperText}</p>}
    </div>
  );
};

// ─── Textarea Component ─────────────────────────────────────────────────────
export interface TextareaProps {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  name?: string;
  id?: string;
  rows?: number;
  cols?: number;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  error?: string;
  helperText?: string;
  maxLength?: number;
  showCharCount?: boolean;
  autoResize?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
}

export const Textarea: React.FC<TextareaProps> = ({
  label,
  value,
  onChange,
  name,
  id,
  rows = 3,
  cols,
  placeholder = "",
  required = false,
  disabled = false,
  readOnly = false,
  className = "",
  error,
  helperText,
  maxLength,
  showCharCount = false,
  autoResize = false,
  onBlur,
  onFocus,
}) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    if (autoResize && textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value, autoResize]);

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label htmlFor={id || name} className={`${labelBase} ${required ? "after:content-['*'] after:ml-1 after:text-red-500" : ""}`}>
          {label}
        </label>
      )}
      <textarea
        ref={textareaRef}
        id={id || name}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        cols={cols}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        maxLength={maxLength}
        onBlur={onBlur}
        onFocus={onFocus}
        className={`
          ${inputBase} resize-y
          ${error ? "border-red-500 focus:ring-red-400" : ""}
          ${autoResize ? "overflow-hidden" : ""}
        `}
      />
      <div className="flex justify-between items-center">
        <div>
          {error && <p className={errorBase}>{error}</p>}
          {helperText && !error && <p className={helperBase}>{helperText}</p>}
        </div>
        {showCharCount && maxLength && (
          <p className="text-xs text-gray-400">
            {value.length}/{maxLength}
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Toggle/Switch Component ────────────────────────────────────────────────
export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
  onLabel?: string;
  offLabel?: string;
}

export const Toggle: React.FC<ToggleProps> = ({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  size = "md",
  className = "",
  onLabel,
  offLabel,
}) => {
  const sizeClasses = {
    sm: "w-8 h-4",
    md: "w-11 h-6",
    lg: "w-14 h-7",
  };

  const knobSize = {
    sm: "w-3 h-3",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  const translateX = {
    sm: checked ? "translate-x-4" : "translate-x-0",
    md: checked ? "translate-x-5" : "translate-x-0",
    lg: checked ? "translate-x-7" : "translate-x-0",
  };

  return (
    <div className={`${className}`}>
      <label className={`flex items-center gap-3 cursor-pointer select-none ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
        <button
          type="button"
          onClick={() => !disabled && onChange(!checked)}
          disabled={disabled}
          className={`
            relative rounded-full transition-colors duration-200 flex-shrink-0
            ${sizeClasses[size]}
            ${checked ? "bg-indigo-600" : "bg-gray-300"}
            ${disabled ? "cursor-not-allowed" : ""}
          `}
        >
          <span className={`
            absolute top-0.5 left-0.5 bg-white rounded-full shadow transition-transform duration-200
            ${knobSize[size]}
            ${translateX[size]}
          `} />
        </button>
        {(label || onLabel || offLabel) && (
          <div>
            {label && <span className="text-sm font-medium text-gray-700">{label}</span>}
            {(onLabel || offLabel) && (
              <span className="text-sm text-gray-500 ml-2">
                {checked ? onLabel : offLabel}
              </span>
            )}
            {description && (
              <p className="text-xs text-gray-500">{description}</p>
            )}
          </div>
        )}
      </label>
    </div>
  );
};

// ─── ColorPicker Component ──────────────────────────────────────────────────
export interface ColorPickerProps {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  className?: string;
  presetColors?: string[];
  showHex?: boolean;
  showOpacity?: boolean;
  disabled?: boolean;
  required?: boolean;
}

const DEFAULT_PRESET_COLORS = [
  "#6366f1", "#ec4899", "#f59e0b", "#10b981", "#ef4444",
  "#8b5cf6", "#06b6d4", "#14b8a6", "#f97316", "#84cc16",
];

export const ColorPicker: React.FC<ColorPickerProps> = ({
  label,
  value,
  onChange,
  className = "",
  presetColors = DEFAULT_PRESET_COLORS,
  showHex = true,
  showOpacity = false,
  disabled = false,
  required = false,
}) => {
  const [opacity, setOpacity] = React.useState(100);

  React.useEffect(() => {
    if (showOpacity && value.includes("rgba")) {
      const match = value.match(/[\d\.]+$/g);
      if (match) setOpacity(parseFloat(match[0]) * 100);
    }
  }, [value, showOpacity]);

  const handleColorChange = (color: string) => {
    if (showOpacity) {
      const rgb = parseInt(color.slice(1), 16);
      const r = (rgb >> 16) & 0xff;
      const g = (rgb >> 8) & 0xff;
      const b = rgb & 0xff;
      onChange(`rgba(${r}, ${g}, ${b}, ${opacity / 100})`);
    } else {
      onChange(color);
    }
  };

  const handleOpacityChange = (newOpacity: number) => {
    setOpacity(newOpacity);
    if (value.startsWith("rgba")) {
      const rgbMatch = value.match(/rgba\(([^,]+),([^,]+),([^,]+)/);
      if (rgbMatch) {
        onChange(`rgba(${rgbMatch[1]},${rgbMatch[2]},${rgbMatch[3]}, ${newOpacity / 100})`);
      }
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className={`${labelBase} ${required ? "after:content-['*'] after:ml-1 after:text-red-500" : ""}`}>
          {label}
        </label>
      )}
      
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value.startsWith("rgba") ? "#6366f1" : value}
          onChange={(e) => handleColorChange(e.target.value)}
          disabled={disabled}
          className="h-10 w-16 rounded-lg border border-gray-200 cursor-pointer p-1 disabled:opacity-60"
        />
        
        {showHex && (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
            placeholder="#000000"
          />
        )}
      </div>
      
      {showOpacity && (
        <div className="space-y-1">
          <label className="text-xs text-gray-500">Opacity: {opacity}%</label>
          <input
            type="range"
            min="0"
            max="100"
            value={opacity}
            onChange={(e) => handleOpacityChange(parseInt(e.target.value))}
            disabled={disabled}
            className="w-full"
          />
        </div>
      )}
      
      <div className="flex gap-2 flex-wrap">
        {presetColors.map(color => (
          <button
            key={color}
            type="button"
            onClick={() => handleColorChange(color)}
            className={`w-8 h-8 rounded-full border-2 transition-all ${
              value === color ? "border-indigo-500 scale-110" : "border-transparent"
            }`}
            style={{ backgroundColor: color }}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Checkbox Component ─────────────────────────────────────────────────────
export interface CheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
  helperText?: string;
  disabled?: boolean;
  indeterminate?: boolean;
  name?: string;
  id?: string;
  value?: string;
  required?: boolean;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked,
  onChange,
  className = "",
  helperText,
  disabled = false,
  indeterminate = false,
  name,
  id,
  value,
  required = false,
}) => {
  const checkboxRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <div className={className}>
      <label className={`flex items-start gap-3 cursor-pointer ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
        <input
          ref={checkboxRef}
          type="checkbox"
          name={name}
          id={id}
          value={value}
          checked={checked}
          onChange={(e) => !disabled && onChange(e.target.checked)}
          disabled={disabled}
          required={required}
          className="mt-0.5 w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 disabled:opacity-60"
        />
        <div className="flex-1">
          <span className="text-sm text-gray-700">{label}</span>
          {helperText && (
            <p className="text-xs text-gray-500 mt-0.5">{helperText}</p>
          )}
        </div>
      </label>
    </div>
  );
};

// ─── Radio Group Component ──────────────────────────────────────────────────
export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
  helperText?: string;
}

export interface RadioGroupProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: RadioOption[];
  className?: string;
  required?: boolean;
  disabled?: boolean;
  inline?: boolean;
  name?: string;
  error?: string;
  helperText?: string;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
  label,
  value,
  onChange,
  options,
  className = "",
  required = false,
  disabled = false,
  inline = false,
  name,
  error,
  helperText,
}) => {
  const groupName = name || `radio-group-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className={`${labelBase} ${required ? "after:content-['*'] after:ml-1 after:text-red-500" : ""}`}>
          {label}
        </label>
      )}
      <div className={`${inline ? "flex gap-4" : "space-y-2"}`}>
        {options.map(option => (
          <label
            key={option.value}
            className={`
              flex items-center gap-2 cursor-pointer
              ${disabled || option.disabled ? "opacity-60 cursor-not-allowed" : ""}
            `}
          >
            <input
              type="radio"
              name={groupName}
              value={option.value}
              checked={value === option.value}
              onChange={(e) => !disabled && !option.disabled && onChange(e.target.value)}
              disabled={disabled || option.disabled}
              className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
            />
            <div>
              <span className="text-sm text-gray-700">{option.label}</span>
              {option.helperText && (
                <p className="text-xs text-gray-500">{option.helperText}</p>
              )}
            </div>
          </label>
        ))}
      </div>
      {error && <p className={errorBase}>{error}</p>}
      {helperText && !error && <p className={helperBase}>{helperText}</p>}
    </div>
  );
};

// ─── File Upload Component ──────────────────────────────────────────────────
export interface FileUploadProps {
  label?: string;
  value?: File | string;
  onChange: (file: File | null) => void;
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in MB
  className?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  preview?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  label,
  value,
  onChange,
  accept = "image/*",
  multiple = false,
  maxSize = 5,
  className = "",
  error,
  helperText,
  disabled = false,
  required = false,
  preview = true,
}) => {
  const [dragActive, setDragActive] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (value && typeof value !== "string" && preview) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [value, preview]);

  const handleFile = (files: FileList | null) => {
    if (!files) return;
    const file = files[0];
    
    if (file.size > maxSize * 1024 * 1024) {
      alert(`File size must be less than ${maxSize}MB`);
      return;
    }
    
    onChange(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFile(e.dataTransfer.files);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className={`${labelBase} ${required ? "after:content-['*'] after:ml-1 after:text-red-500" : ""}`}>
          {label}
        </label>
      )}
      
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center
          ${dragActive ? "border-indigo-500 bg-indigo-50" : "border-gray-300"}
          ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => handleFile(e.target.files)}
          disabled={disabled}
          className="hidden"
        />
        
        <div className="space-y-2">
          <div className="text-4xl">📁</div>
          <p className="text-sm text-gray-600">
            Drag & drop or click to upload
          </p>
          <p className="text-xs text-gray-400">
            {accept.split(",").join(", ")} up to {maxSize}MB
          </p>
        </div>
      </div>
      
      {preview && previewUrl && (
        <div className="relative">
          <img src={previewUrl} alt="Preview" className="max-h-32 rounded-lg" />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 text-xs"
          >
            ✕
          </button>
        </div>
      )}
      
      {error && <p className={errorBase}>{error}</p>}
      {helperText && !error && <p className={helperBase}>{helperText}</p>}
    </div>
  );
};

// ─── Form Row (Grid Layout) ─────────────────────────────────────────────────
export interface FormRowProps {
  children: React.ReactNode;
  className?: string;
  columns?: number;
  gap?: number;
}

export const FormRow: React.FC<FormRowProps> = ({ 
  children, 
  className = "", 
  columns = 2,
  gap = 4 
}) => (
  <div className={`grid grid-cols-1 md:grid-cols-${columns} gap-${gap} ${className}`}>
    {children}
  </div>
);

// ─── Form Section (Grouped Fields) ─────────────────────────────────────────
export interface FormSectionProps {
  title: string;
  icon?: ReactNode;
  children: React.ReactNode;
  className?: string;
  description?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

export const FormSection: React.FC<FormSectionProps> = ({ 
  title, 
  icon, 
  children, 
  className = "",
  description,
  collapsible = false,
  defaultCollapsed = false,
}) => {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);

  return (
    <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${className}`}>
      <div 
        className={`
          bg-gradient-to-r from-indigo-50 to-purple-50 px-5 py-3 border-b border-gray-200
          ${collapsible ? "cursor-pointer" : ""}
        `}
        onClick={() => collapsible && setCollapsed(!collapsed)}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            {icon && <span className="text-xl">{icon}</span>}
            {title}
          </h3>
          {collapsible && (
            <span className="text-gray-400">{collapsed ? "▼" : "▲"}</span>
          )}
        </div>
        {description && !collapsed && (
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        )}
      </div>
      {!collapsed && (
        <div className="p-5">
          {children}
        </div>
      )}
    </div>
  );
};

// ─── Form Actions (Buttons) ────────────────────────────────────────────────
export interface FormActionsProps {
  onCancel: () => void;
  onSave: () => void;
  onReset?: () => void;
  isSaving?: boolean;
  isDeleting?: boolean;
  onDelete?: () => void;
  cancelText?: string;
  saveText?: string;
  deleteText?: string;
  resetText?: string;
  className?: string;
  align?: "left" | "center" | "right";
  showReset?: boolean;
  showDelete?: boolean;
}

export const FormActions: React.FC<FormActionsProps> = ({ 
  onCancel, 
  onSave, 
  onReset,
  isSaving = false,
  isDeleting = false,
  onDelete,
  cancelText = "Cancel",
  saveText = "Save",
  deleteText = "Delete",
  resetText = "Reset",
  className = "",
  align = "right",
  showReset = false,
  showDelete = false,
}) => {
  const alignClasses = {
    left: "justify-start",
    center: "justify-center",
    right: "justify-end",
  };

  return (
    <div className={`flex gap-3 pt-4 border-t border-gray-200 sticky bottom-0 bg-white py-4 ${alignClasses[align]} ${className}`}>
      {showReset && onReset && (
        <button
          type="button"
          onClick={onReset}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          {resetText}
        </button>
      )}
      
      <button
        type="button"
        onClick={onCancel}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        {cancelText}
      </button>
      
      {showDelete && onDelete && (
        <button
          type="button"
          onClick={onDelete}
          disabled={isDeleting}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isDeleting ? "Deleting..." : deleteText}
        </button>
      )}
      
      <button
        type="button"
        onClick={onSave}
        disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSaving ? "Saving..." : saveText}
      </button>
    </div>
  );
};

// ─── Form Field (Wrapper for consistent spacing) ───────────────────────────
export interface FormFieldProps {
  children: React.ReactNode;
  className?: string;
  required?: boolean;
  error?: string;
  helperText?: string;
}

export const FormField: React.FC<FormFieldProps> = ({
  children,
  className = "",
  required,
  error,
  helperText,
}) => (
  <div className={`space-y-1 ${className}`}>
    {children}
    {error && <p className={errorBase}>{error}</p>}
    {helperText && !error && <p className={helperBase}>{helperText}</p>}
  </div>
);

// Default export
const FormFields = {
  Input,
  Select,
  Textarea,
  Toggle,
  ColorPicker,
  Checkbox,
  RadioGroup,
  FileUpload,
  FormRow,
  FormSection,
  FormActions,
  FormField,
};

export default FormFields;